/**
 * `/quote` — Quote module home page (M-QUOTE.3.b).
 *
 * Five-tile launchpad mirroring the leads/content home pages: catalog,
 * playbooks, documents, drafts queue, follow-up sequences. Each tile
 * links into the dedicated sub-page.
 */
import { FileText, Inbox, Package, PlayCircle, Repeat } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export function QuoteHomePage() {
  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Workspace</p>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Quote</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          AI quote desk — capture leads, draft quote-ready responses, automate follow-ups.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <QuoteTile
          to="/quote/drafts"
          icon={<Inbox className="h-5 w-5" />}
          title="Drafts queue"
          description="Approve or reject AI-drafted replies before they go out."
        />
        <QuoteTile
          to="/quote/documents"
          icon={<FileText className="h-5 w-5" />}
          title="Documents"
          description="Upload PDFs, proposals, and past quotes for RAG-backed enrichment."
        />
        <QuoteTile
          to="/quote/catalog"
          icon={<Package className="h-5 w-5" />}
          title="Service catalog"
          description="What you sell — names, price ranges, durations."
        />
        <QuoteTile
          to="/quote/playbooks"
          icon={<PlayCircle className="h-5 w-5" />}
          title="Playbooks"
          description="Per-vertical prompt overrides used by the AI workflows."
        />
        <QuoteTile
          to="/quote/sequences"
          icon={<Repeat className="h-5 w-5" />}
          title="Follow-up sequences"
          description="Multi-step drips that chase stale quote opportunities."
        />
      </div>
    </div>
  );
}

function QuoteTile({
  to,
  icon,
  title,
  description,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2 text-primary">
          {icon}
          <CardTitle>{title}</CardTitle>
        </div>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <Link to={to}>
          <Button variant="outline" size="sm">
            Open
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}
